import { useState } from 'react';
import { api, ApiError, type User } from '@/lib/api';
import { AVATAR_COLORS } from '@shared/protocol.js';
import { Alert, Modal, initials, useToast } from './kit';

/** Profile, password and account-deletion, all in one place. */
export default function AccountModal({
  user, onClose, onUserChange, onSignOut, onUpgrade,
}: {
  user: User;
  onClose: () => void;
  onUserChange: (user: User) => void;
  onSignOut: () => void;
  onUpgrade: () => void;
}) {
  const [tab, setTab] = useState<'profile' | 'security'>('profile');
  const [username, setUsername] = useState(user.username);
  const [avatarColor, setAvatarColor] = useState(user.avatarColor);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  const dirty = username.trim() !== user.username || avatarColor !== user.avatarColor;

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  const saveProfile = () => run(async () => {
    const res = await api.updateMe({ username: username.trim(), avatarColor });
    onUserChange(res.user);
    toast('Profile saved.');
  });

  const changePassword = () => {
    if (newPassword !== confirmPassword) {
      setError('Those passwords do not match.');
      return;
    }
    run(async () => {
      await api.changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast('Password changed. Other devices were signed out.');
    });
  };

  const deleteAccount = () => run(async () => {
    await api.deleteAccount(deletePassword);
    toast('Account deleted.');
    onSignOut();
  });

  return (
    <Modal
      title="Your account"
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>Close</button>
          {tab === 'profile' ? (
            <button type="button" className="btn btn--accent" onClick={saveProfile} disabled={busy || !dirty}>
              {busy ? <span className="spinner" /> : 'Save changes'}
            </button>
          ) : null}
        </>
      )}
    >
      <div className="tabs" style={{ marginBottom: 18 }}>
        <button type="button" className="tab" data-active={tab === 'profile'} onClick={() => { setTab('profile'); setError(''); }}>
          Profile
        </button>
        <button type="button" className="tab" data-active={tab === 'security'} onClick={() => { setTab('security'); setError(''); }}>
          Password &amp; security
        </button>
      </div>

      {error ? <Alert tone="error">{error}</Alert> : null}

      {tab === 'profile' ? (
        <>
          <div className="row" style={{ marginBottom: 18 }}>
            <span
              className="chip__dot"
              style={{ background: avatarColor, width: 56, height: 56, fontSize: 20 }}
            >
              {initials(username || user.username)}
            </span>
            <div className="grow">
              <div style={{ fontWeight: 700 }}>{username || user.username}</div>
              <div className="tiny muted selectable">{user.email}</div>
            </div>
          </div>

          <div className="field">
            <label htmlFor="ac-name">Display name</label>
            <input
              id="ac-name"
              className="input"
              value={username}
              maxLength={24}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <div className="field">
            <label>Your colour</label>
            <div className="swatches" style={{ gridTemplateColumns: 'repeat(auto-fill, 40px)' }}>
              {AVATAR_COLORS.map((color: string) => (
                <button
                  key={color}
                  type="button"
                  className="swatch"
                  style={{ background: color }}
                  data-active={avatarColor === color}
                  onClick={() => setAvatarColor(color)}
                  aria-label={`Use ${color}`}
                />
              ))}
            </div>
            <div className="tiny muted" style={{ marginTop: 8 }}>
              This is the colour of your cursor and viewport frame for everyone else.
            </div>
          </div>

          <div className="setting">
            <div className="setting__text">
              <div className="setting__label">Email</div>
              <div className="setting__hint">
                {user.emailVerified ? 'Confirmed.' : 'Not confirmed yet — needed for password resets.'}
              </div>
            </div>
            {!user.emailVerified ? (
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                onClick={() => api.resendVerification()
                  .then(() => toast('Confirmation email sent.'))
                  .catch(() => toast('Could not send that right now.', 'error'))}
              >
                Resend
              </button>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Change password</h4>
          <div className="field">
            <label htmlFor="ac-cur">Current password</label>
            <input
              id="ac-cur"
              className="input"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ac-new">New password</label>
            <input
              id="ac-new"
              className="input"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ac-new2">Confirm new password</label>
            <input
              id="ac-new2"
              className="input"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="btn btn--block"
            onClick={changePassword}
            disabled={busy || !currentPassword || !newPassword}
          >
            {busy ? <span className="spinner" /> : 'Change password'}
          </button>

          <div className="divider" />

          <div className="setting">
            <div className="setting__text">
              <div className="setting__label">Sign out everywhere</div>
              <div className="setting__hint">Ends every other session signed in as you.</div>
            </div>
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              onClick={() => api.signOutEverywhere()
                .then(() => toast('Other sessions signed out.'))
                .catch(() => toast('Could not do that right now.', 'error'))}
            >
              Sign out
            </button>
          </div>

          <div className="divider" />

          <h4 style={{ margin: '0 0 6px', fontSize: 14, color: 'var(--danger)' }}>Delete account</h4>
          <p className="tiny muted" style={{ marginTop: 0 }}>
            Permanently removes your account, the sketchpads you host and everything drawn on them.
          </p>
          {confirmingDelete ? (
            <>
              <div className="field">
                <label htmlFor="ac-del">Enter your password to confirm</label>
                <input
                  id="ac-del"
                  className="input"
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="row">
                <button type="button" className="btn btn--ghost grow" onClick={() => setConfirmingDelete(false)}>
                  Keep my account
                </button>
                <button
                  type="button"
                  className="btn btn--danger grow"
                  onClick={deleteAccount}
                  disabled={busy || !deletePassword}
                >
                  Delete forever
                </button>
              </div>
            </>
          ) : (
            <button type="button" className="btn btn--ghost btn--block" onClick={() => setConfirmingDelete(true)}>
              Delete my account
            </button>
          )}
        </>
      )}

      <div className="divider" />
      <div className="row row--between row--wrap" style={{ gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <strong>{user.plan === 'pro' ? 'Ink Share Pro' : 'Free plan'}</strong>
          <div className="tiny muted">
            {user.plan === 'pro'
              ? `${user.limits.drawers} people per sketchpad · up to ${user.limits.pads} saved`
              : `${user.limits.drawers} people per sketchpad · ${user.limits.pads} saved sketchpads`}
          </div>
        </div>
        <button type="button" className={user.plan === 'pro' ? 'btn btn--sm btn--ghost' : 'btn btn--sm btn--accent'} onClick={onUpgrade}>
          {user.plan === 'pro' ? 'Manage plan' : 'Go Pro'}
        </button>
      </div>

      {/* Reachable from inside the app, which is what the app stores expect —
          and useful anywhere, since the join code is not the only thing people
          want to know about what is stored. */}
      <p className="tiny muted" style={{ margin: '20px 0 0', textAlign: 'center' }}>
        <a href="/privacy" target="_blank" rel="noreferrer noopener">Privacy policy</a>
        {' · '}
        <a href="/delete-account" target="_blank" rel="noreferrer noopener">Deleting your data</a>
      </p>
    </Modal>
  );
}
